import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
import dayjs from "dayjs";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Market } from "../../../../../store/slices/searchSlice";
import { useTypedSelector } from "../../../../../store/store";
import { currencyToFormat } from "../../../../../utils/helpers/currency";
import CustomCarousel from "../../../../shared/Carousel";
import Collapsible from "../../../../shared/Collapsible";

import styles from "./index.module.scss";

function ItineraryModal() {
  const { t } = useTranslation();

  const { cruise } = useTypedSelector((state) => state.search);
  const { rooms } = useTypedSelector((state) => state.rooms);
  const { date_format } = useTypedSelector((state) => state.environment);

  const { strip_no_stop_itinerary_items } = useTypedSelector(
    (state) => state.environment,
  );

  const price = useMemo(() => {
    if (!cruise) {
      return 0;
    }

    const prices = Object.values(cruise.markets)
      .filter((market) => typeof market === "object" && market !== null)
      .map((market) => parseFloat((market as Market).price))
      .filter((price) => !isNaN(price) && price !== 0);

    return Math.min(...prices);
  }, []);

  const validateTime = (value: string | null) => {
    if (value === "00:00" && strip_no_stop_itinerary_items) {
      return false;
    }

    return Boolean(value);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.imagesContainer}>
          <div
            className={classNames(styles.images, {
              [styles.images_twoColumns]: cruise?.cruise.map,
            })}>
            <CustomCarousel
              items={cruise?.ship.images ?? []}
              slideClassName={styles.image}
            />

            {cruise?.cruise.map && (
              <img
                src={cruise?.cruise.map}
                className={styles.image}
                alt="cruise map"
              />
            )}
          </div>

          <p className={styles.name}>{cruise?.cruise.name ?? ""}</p>
        </div>

        <table className={styles.details}>
          <tbody>
            <tr>
              <td>{t("SHIP NAME")}</td>
              <td>{cruise?.ship.name ?? ""}</td>
            </tr>

            <tr>
              <td>{t("DEPARTS")}</td>
              <td>{dayjs(cruise?.embark ?? null).format(date_format)}</td>
            </tr>
          </tbody>
        </table>

        <div className={styles.price}>
          <span className={styles.price_from}>{t("From")}</span>

          <span className={styles.price_value}>
            {currencyToFormat(
              price,
              rooms?.[1]?.pricing?.payment_schedule?.[0]?.currency ??
                cruise?.markets?.currency ??
                "USD",
            )}
          </span>
        </div>

        <div className={styles.itinerary}>
          {cruise?.cruise.itinerary.map((it) => {
            const { port, arrive_time, depart_time, description } = it;

            if (!validateTime(arrive_time) && !validateTime(depart_time)) {
              return <Fragment key={nanoid()} />;
            }

            return (
              <Collapsible
                key={nanoid()}
                className={styles.svg_ico}
                renderHeader={() => (
                  <span className={styles.itineraryTitle}>{port}</span>
                )}>
                <table className={styles.details}>
                  <tbody>
                    {validateTime(arrive_time) && (
                      <tr>
                        <td>{t("ARRIVES")}</td>
                        <td>{arrive_time}</td>
                      </tr>
                    )}

                    {validateTime(depart_time) && (
                      <tr>
                        <td>{t("DEPARTS")}</td>
                        <td>{depart_time}</td>
                      </tr>
                    )}

                    {description && (
                      <tr className={styles.dividerRow}>
                        <td colSpan={2}>
                          <div className={styles.customDivider}></div>
                        </td>
                      </tr>
                    )}

                    {description && (
                      <tr className={styles.description}>
                        <td>{t("DESCRIPTION")}</td>
                        <td dangerouslySetInnerHTML={{ __html: description }} />
                      </tr>
                    )}
                  </tbody>
                </table>
              </Collapsible>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ItineraryModal;
